import React, { useMemo, useEffect, useState } from 'react';
import { useFormik, getIn } from 'formik';
import * as Yup from 'yup';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings, saveProviderEducation } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import DocumentUpload from '../Common/DocumentUpload';
import CustomDatePicker from '../Common/CustomDatePicker';
import CustomPhoneInput from '../Common/CustomPhoneInput';

const SETTING_TYPE_IDS = {
  education_type: 21,
  degree_earned: 22,
  program_completed: 52,
};
const validationSchema = Yup.object({
  education_type: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue)))
    .nullable(),
  institution_name: Yup.string().trim().nullable(),
  specialty: Yup.string().trim().nullable(),
  degree_earned: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue)))
    .nullable(),
  program_completed: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue)))
    .nullable(),
  start_date: Yup.date().nullable().typeError('Invalid date format'),
  end_date: Yup.date().nullable().typeError('Invalid date format'),
  status_id: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : Number(originalValue)))
    .nullable(),
  document: Yup.mixed().nullable(),
  contact: Yup.object({
    contact_name: Yup.string().trim().nullable(),
    contact_email: Yup.string().email('Invalid email').nullable(),
    contact_phone: Yup.string().trim().nullable(),
    contact_fax: Yup.string().trim().nullable(),
  }).nullable(),
  address: Yup.object({
    address_line_1: Yup.string().trim().nullable(),
    address_line_2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    zip_code: Yup.string().trim().nullable(),
    county: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    email: Yup.string().email('Invalid email').nullable(),
    phone: Yup.string().trim().nullable(),
    fax: Yup.string().trim().nullable(),
  }).nullable(),
});

const emptyForm = {
  id: null,
  education_type: '',
  institution_name: '',
  specialty: '',
  degree_earned: '',
  program_completed: '',
  start_date: null,
  end_date: null,
  status_id: '',
  document: null,
  contact: {
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_fax: '',
  },
  address: {
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    county: '',
    country: '',
    email: '',
    phone: '',
    fax: '',
  },
};

const EDUCATION_TYPE_OPTIONS = [
  { id: 1, label: 'Medical School' },
  { id: 2, label: 'Residency' },
  { id: 3, label: 'Fellowship' },
  { id: 4, label: 'Internship' },
  { id: 5, label: 'Other' },
];

const DEGREE_OPTIONS = [
  { id: 1, label: 'MD (Doctor of Medicine)' },
  { id: 2, label: 'DO (Doctor of Osteopathic Medicine)' },
  { id: 3, label: 'PhD' },
  { id: 4, label: 'DDS' },
  { id: 5, label: 'DVM' },
  { id: 6, label: 'Other' },
];

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
];

const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const getOrganizationIdFromStorage = () => {
  try {
    return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read organization ID from localStorage:', error);
    return null;
  }
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.code ?? item.name ?? '',
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? item.Id ?? ''),
});

function mapApiToForm(educationData) {
  if (!educationData) return emptyForm;
  return {
    id: educationData.id || null,
    education_type: educationData.education_type || '',
    institution_name: educationData.institution_name || '',
    specialty: educationData.specialty || '',
    degree_earned: educationData.degree_earned || '',
    program_completed: educationData.program_completed ?? false,
    start_date: educationData.start_date || '',
    end_date: educationData.end_date || '',
    status_id: educationData.status_id || '',
    provider_id:
      educationData.provider_id ||
      educationData.providerId ||
      educationData.providerId ||
      null,
    organization_id:
      educationData.organization_id ||
      educationData.organizationId ||
      educationData.organizationId ||
      null,
    file_id: educationData.file_id || educationData.fileId || null,
    contact_id: educationData.contact_id || educationData.contactId || null,
    type: educationData.type || (educationData.id ? 'update' : 'create'),
    document:
      educationData.document ??
      (educationData.file_name ? { file_name: educationData.file_name } : null) ??
      (educationData.fileName ? { file_name: educationData.fileName } : null) ??
      null,
    contact: {
      contact_name:
        educationData.contact?.contact_name ||
        educationData.contact_name ||
        educationData.program_director_name ||
        '',
      contact_email:
        educationData.contact?.contact_email ||
        educationData.contact_email ||
        educationData.program_director_email ||
        '',
      contact_phone:
        educationData.contact?.contact_phone ||
        educationData.contact_phone ||
        educationData.program_director_phone ||
        '',
      contact_fax:
        educationData.contact?.contact_fax ||
        educationData.contact_fax ||
        educationData.program_director_fax ||
        educationData.fax ||
        '',
    },
    address: {
      address_line_1:
        educationData.address?.address_line_1 ||
        educationData.address_line_1 ||
        educationData.address?.street ||
        educationData.street ||
        '',
      address_line_2:
        educationData.address?.address_line_2 ||
        educationData.address_line_2 ||
        educationData.address?.street2 ||
        educationData.street2 ||
        '',
      city:
        educationData.address?.city ||
        educationData.city ||
        educationData.address?.City ||
        '',
      state:
        educationData.address?.state ||
        educationData.state ||
        educationData.address?.State ||
        '',
      zip_code:
        educationData.address?.zip_code ||
        educationData.address?.zipcode ||
        educationData.address?.Zipcode ||
        educationData.zip_code ||
        educationData.zipcode ||
        educationData.address?.zip ||
        '',
      county:
        educationData.address?.county || educationData.county || '',
      country:
        educationData.address?.country ||
        educationData.country ||
        educationData.address?.Country ||
        '',
      email: educationData.address?.email || educationData.email || '',
      phone: educationData.address?.phone || educationData.phone || '',
      fax: educationData.address?.fax || educationData.fax || '',
    },
  };
}

function getFieldError(fieldName, formik) {
  const touched = getIn(formik.touched, fieldName);
  const error = getIn(formik.errors, fieldName);
  return touched && error ? error : null;
}

export default function EducationModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  providerId,
  isLoading,
}) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({
    education_type: [],
    degree_earned: [],
    program_completed: [],
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const initialFormValues = useMemo(() => {
    return initialData ? mapApiToForm(initialData) : emptyForm;
  }, [initialData]);

  const educationTypeOptions = providerSettings.education_type.length
    ? providerSettings.education_type
    : EDUCATION_TYPE_OPTIONS;

  const degreeOptions = providerSettings.degree_earned.length
    ? providerSettings.degree_earned
    : DEGREE_OPTIONS;

  const programCompletedOptions = providerSettings.program_completed.length
    ? providerSettings.program_completed
    : [
        { value: 1, label: 'Yes' },
        { value: 0, label: 'No' },
      ];

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, actions) => {
      try {
        actions.setSubmitting(true);

        const payload = {
          id: values.id || null,
          education_type: values.education_type || null,
          institution_name: values.institution_name || null,
          specialty: values.specialty || null,
          degree_earned: values.degree_earned || null,
          program_completed:
            values.program_completed === ''
              ? null
              : values.program_completed ?? null,
          start_date: values.start_date || null,
          end_date: values.end_date || null,
          program_director_name:
            values.contact?.contact_name || values.program_director_name || null,
          program_director_email:
            values.contact?.contact_email || values.program_director_email || null,
          program_director_phone:
            values.contact?.contact_phone || values.program_director_phone || null,
          program_director_fax:
            values.contact?.contact_fax || values.program_director_fax || null,
          provider_id: providerId || values.provider_id || null,
          organization_id:
            values.organization_id ||
            values.organizationId ||
            getOrganizationIdFromStorage() ||
            null,
          status_id: values.status_id || null,
          file_id: values.file_id || null,
          contact_id: values.contact_id || null,
          address_id: values.address_id || null,
          address: {
            street: values.address?.address_line_1 || null,
            street2: values.address?.address_line_2 || null,
            city: values.address?.city || null,
            state: values.address?.state || null,
            country: values.address?.country || null,
            zipcode: values.address?.zip_code || null,
          },
          type: values.type || (values.id ? 'update' : 'create'),
        };

        const response = await saveProviderEducation(token, {
          ...payload,
          document: values.document,
        });

        if (response?.is_success) {
          onSave(response.data ?? values);
        } else if (response?.status_code === 500 && response?.is_success === false) {
          console.error(response);
          toast.error('Internal Server Error. Please Contact Support Team.');
        } else {
          toast.error(response?.message || 'Failed to save education information');
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message || 'Failed to save education information');
      } finally {
        actions.setSubmitting(false);
      }
    },
  });

  const loadProviderSettings = async () => {
    try {
      setSettingsLoading(true);
      const entries = Object.entries(SETTING_TYPE_IDS);
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success) {
              return {
                key,
                options: normalizeArray(response?.data).map(mapProviderSettingItem),
              };
            } else if (response?.status_code === 500 && response?.is_success === false) {
              console.error(response);
              toast.error('Internal Server Error. Please Contact Support Team.');
            } else {
              toast.error(response?.message || 'Failed to load provider settings');
            }
          } catch (error) {
            console.error(`Failed to load provider settings for ${key}:`, error);
            toast.error('Failed to load provider settings');
          }
          return { key, options: [] };
        })
      );
      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
  }, [token]);

  const addressFieldValues = {
    street: formik.values.address.address_line_1,
    street2: formik.values.address.address_line_2,
    city: formik.values.address.city,
    state: formik.values.address.state,
    country: formik.values.address.country,
    zip: formik.values.address.zip_code,
  };

  const addressFieldErrors = {
    street: formik.touched.address?.address_line_1 && formik.errors.address?.address_line_1 ? formik.errors.address.address_line_1 : undefined,
    street2: formik.touched.address?.address_line_2 && formik.errors.address?.address_line_2 ? formik.errors.address.address_line_2 : undefined,
    city: formik.touched.address?.city && formik.errors.address?.city ? formik.errors.address.city : undefined,
    state: formik.touched.address?.state && formik.errors.address?.state ? formik.errors.address.state : undefined,
    country: formik.touched.address?.country && formik.errors.address?.country ? formik.errors.address.country : undefined,
    zip: formik.touched.address?.zip_code && formik.errors.address?.zip_code ? formik.errors.address.zip_code : undefined,
  };

  const addressFieldTouched = {
    street: formik.touched.address?.address_line_1,
    street2: formik.touched.address?.address_line_2,
    city: formik.touched.address?.city,
    state: formik.touched.address?.state,
    country: formik.touched.address?.country,
    zip: formik.touched.address?.zip_code,
  };

  const handleAddressFieldChange = (fieldName, value) => {
    const rawField = fieldName.replace(/^address\./, '');
    const mappedField = rawField === 'street'
      ? 'address.address_line_1'
      : rawField === 'street2'
        ? 'address.address_line_2'
        : rawField === 'zip'
          ? 'address.zip_code'
          : `address.${rawField}`;

    formik.setFieldValue(mappedField, value);
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          {/* Header */}
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">
              {initialData?.id ? 'Edit Education Record' : 'Add Education Record'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isLoading}
            >

            </button>
          </div>

          {/* Body */}
          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="fw-semibold hc-text-primary">Education Details</h6>
                  </div>

                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Education Type"
                      placeholder="Select Option"
                      options={educationTypeOptions.map((option) => ({
                        value: option.value ?? option.id,
                        label: option.label,
                      }))}
                      value={formik.values.education_type}
                      onChange={(value) => {
                        formik.setFieldValue('education_type', value || '');
                        formik.setFieldTouched('education_type', true);
                      }}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={getFieldError('education_type', formik) ? 'is-invalid' : ''}
                      emptyMessage="No education types found"
                    />
                    <div className="invalid-feedback d-block">{getFieldError('education_type', formik)}</div>
                  </div>

                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Degree Earned"
                      placeholder="Select Option"
                      options={degreeOptions.map((option) => ({
                        value: option.value ?? option.id,
                        label: option.label,
                      }))}
                      value={formik.values.degree_earned}
                      onChange={(value) => {
                        formik.setFieldValue('degree_earned', value || '');
                        formik.setFieldTouched('degree_earned', true);
                      }}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={getFieldError('degree_earned', formik) ? 'is-invalid' : ''}
                      emptyMessage="No degrees found"
                    />
                    <div className="invalid-feedback d-block">{getFieldError('degree_earned', formik)}</div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Institution Name</label>
                    <input
                      type="text"
                      placeholder="Enter institution name"
                      className={`form-control rounded-3 shadow-sm ${getFieldError('institution_name', formik)
                          ? 'is-invalid'
                          : ''
                        }`}
                      name="institution_name"
                      value={formik.values.institution_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {getFieldError('institution_name', formik) && (
                      <div className="invalid-feedback d-block">
                        {getFieldError('institution_name', formik)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Specialty/Major</label>
                    <input
                      type="text"
                      placeholder="Enter specialty/major"
                      className={`form-control rounded-3 shadow-sm ${getFieldError('specialty', formik) ? 'is-invalid' : ''
                        }`}
                      name="specialty"
                      value={formik.values.specialty}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {getFieldError('specialty', formik) && (
                      <div className="invalid-feedback d-block">
                        {getFieldError('specialty', formik)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-12 col-lg-6">
                    <SearchableDropdown
                      label="Program Completed"
                      placeholder="Select Option"
                      options={programCompletedOptions.map((option) => ({
                        value: option.value ?? option.id,
                        label: option.label,
                      }))}
                      value={formik.values.program_completed}
                      onChange={(value) => {
                        formik.setFieldValue('program_completed', value || '');
                        formik.setFieldTouched('program_completed', true);
                      }}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={getFieldError('program_completed', formik) ? 'is-invalid' : ''}
                      emptyMessage="No completion status found"
                    />
                    <div className="invalid-feedback d-block">{getFieldError('program_completed', formik)}</div>
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="fw-semibold hc-text-primary">Program Timeline</h6>
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">Start Date</label>

                    <CustomDatePicker
                      className={`form-control rounded-3 shadow-sm ${getFieldError('start_date', formik) ? 'is-invalid' : ''
                        }`}
                      name="start_date"
                      formik={formik}
                    />

                    {/* <input
                      type="date"
                      className={`form-control rounded-3 shadow-sm ${getFieldError('start_date', formik) ? 'is-invalid' : ''
                        }`}
                      name="start_date"
                      value={formik.values.start_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    /> */}

                    {getFieldError('start_date', formik) && (
                      <div className="invalid-feedback d-block">
                        {getFieldError('start_date', formik)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6 col-lg-4">
                    <label className="form-label fw-semibold">End Date</label>

                    <CustomDatePicker
                      className={`form-control rounded-3 shadow-sm ${getFieldError('end_date', formik) ? 'is-invalid' : ''
                        }`}
                      name="end_date"
                      formik={formik}
                    />

                    {/* <input
                      type="date"
                      className={`form-control rounded-3 shadow-sm ${getFieldError('end_date', formik) ? 'is-invalid' : ''
                        }`}
                      name="end_date"
                      value={formik.values.end_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    /> */}
                    {getFieldError('end_date', formik) && (
                      <div className="invalid-feedback d-block">
                        {getFieldError('end_date', formik)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-12 col-lg-4">
                    <label className="form-label fw-semibold">Status</label>
                    <select
                      className={`form-select rounded-3 shadow-sm ${getFieldError('status_id', formik) ? 'is-invalid' : ''
                        }`}
                      name="status_id"
                      value={formik.values.status_id}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="">Select Option</option>
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {getFieldError('status_id', formik) && (
                      <div className="invalid-feedback d-block">
                        {getFieldError('status_id', formik)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="education-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="fw-semibold hc-text-primary">Contact Information</h6>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Program Director Name</label>
                    <input
                      type="text"
                      placeholder="Enter program director name"
                      className="form-control rounded-3 shadow-sm"
                      name="contact.contact_name"
                      value={formik.values.contact.contact_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Program Director Email</label>
                    <input
                      type="email"
                      placeholder="Enter program director email"
                      className={`form-control rounded-3 shadow-sm ${getFieldError('contact.contact_email', formik)
                          ? 'is-invalid'
                          : ''
                        }`}
                      name="contact.contact_email"
                      value={formik.values.contact.contact_email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {getFieldError('contact.contact_email', formik) && (
                      <div className="invalid-feedback d-block">
                        {getFieldError('contact.contact_email', formik)}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Program Director Phone</label>

                    {/* <div>
                    Current phone:{" "}
                    {formik.values.contact?.contact_phone || "EMPTY"}
                  </div> */}

                    {/* <CustomPhoneInput
                      name="contact.contact_phone"
                      value={formik.values.contact?.contact_phone || ""}
                      onChange={(phone) => {
                        formik.setFieldValue(
                          "contact.contact_phone",
                          phone
                        );
                      }}
                      defaultCountry="us"
                      placeholder="Enter contact phone"
                    /> */}

                    <input
                      type="tel"
                      placeholder="Enter program director phone"
                      className="form-control rounded-3 shadow-sm"
                      name="contact.contact_phone"
                      value={formik.values.contact.contact_phone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Program Director Fax</label>
                    <input
                      type="tel"
                      placeholder="Enter program director fax"
                      className="form-control rounded-3 shadow-sm"
                      name="contact.contact_fax"
                      value={formik.values.contact.contact_fax}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <div className="row g-3">
                  <div className="col-12">
                    <h6 className="fw-semibold hc-text-primary">Address Information</h6>
                  </div>

                  <div className="col-12">
                    <AddressSelectorFields
                      title="Address Information"
                      fieldPrefix="address"
                      values={addressFieldValues}
                      errors={addressFieldErrors}
                      touched={addressFieldTouched}
                      onFieldChange={handleAddressFieldChange}
                      disabled={false}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-light rounded-3 px-4"
                onClick={onClose}
                disabled={isLoading || formik.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn hc-bg-primary text-white rounded-3 px-4"
                disabled={isLoading || formik.isSubmitting || !formik.isValid}
              >
                {isLoading || formik.isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
